import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Tab,
  Tabs
} from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormProvider, useForm } from 'react-hook-form';
import {
  fetchEmployeeScreening,
  listScreenings,
  resetPeopleState,
  saveEmployeeDetails,
  Screening
} from '../../../../slices/payrolslices/PeopleSlice';
import BankAndSuperTab from './BankAndSuperTab';
import { employeeDetailsDefaults } from './employeeDetailsDefaults';
import { employeeDetailsSchema } from './employeeDetailsSchema';
import EmploymentTab from './EmploymentTab';
import OpeningBalancesTab from './OpeningBalancesTab';
import PayRatesTab from './PayRatesTab';
import PersonalTab from './PersonalTab';
import TaxTab from './TaxTab';
import TemplateTab from './TemplateTab';

export interface EmployeeDetailsForm {
  personal: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    gender: string;
    address1: string;
    address2?: string;
    suburb: string;
    state: string;
    postcode: string;
  };
  bankAndSuper: {
    paymentMethod: string;
    accountName?: string;
    bank?: string;
    bsb?: string;
    accountNumber?: string;
    superFundType: string;
    superContributionRate: string;
    superCompany: string;
    smsfsName?: string;
    smsfsAbn?: string;
    electronicServiceAddress?: string;
    employerNumber?: string;
    smsfsBankAccountName?: string;
    smsfsBsb?: string;
    smsfsAccountNumber?: string;
  };
  employment: {
    jobTitle: string;
    startDate: string | null;
    employmentType: string;
    weeklyWorkHours: string;
    roles: string;
    payPeriod: string;
    leaveLoading: string;
  };
  payRates: {
    basePayRate: string;
    basePayRateType: string;
    saturdayRate: string;
    saturdayRateType: string;
    sundayRate: string;
    sundayRateType: string;
    publicHolidayRate: string;
    publicHolidayRateType: string;
    additionalPublicHoliday: string;
    additionalPublicHolidayRateType: string;
    nightShiftRate: string;
    nightShiftRateType: string;
    nightShiftStartTime: string;
    nightShiftEndTime: string;
    holidays: Array<{ id: number; name: string; date: string; enabled: boolean }>;
  };
  tax: {
    dateOfBirth: string;
    statusOfResidency: string;
    incomeType: string;
    tfnExemption: string;
    taxFileNumber: string;
    claimTaxFreeThreshold: boolean;
    hasStudyAndTrainingLoan: boolean;
    isHorticulturistOrShearer: boolean;
    hasChildSupportObligations: boolean;
  };
  openingBalances: {
    asAtDate?: string;
    leaveBalances?: {
      annualLeaveBalance?: number;
      sickLeaveBalance?: number;
    };
    ordinaryEarnings?: {
      ordinaryTimeEarnings?: number;
      superGuarantee?: number;
      tax?: number;
      bonusesCommission?: number;
      directorsFee?: number;
      employerContributionsRESC?: number;
      superSalarySacrifice?: number;
      reimbursement?: number;
      overtime?: number;
    };
    paidLeave?: {
      annualLeave?: number;
      sickLeave?: number;
      longServiceLeave?: number;
      paidParentalLeave?: number;
      ancillaryAndDefenceLeave?: number;
      workersCompensation?: number;
    };
    allowance?: {
      awardTransportPayments?: number;
      centsPerKM?: number;
      laundry?: number;
      overtimeMeal?: number;
      qualifications?: number;
      task?: number;
      tool?: number;
      travel?: number;
      jobKeeperTopUp?: number;
      otherGeneral?: number;
      otherHomeOffice?: number;
      otherNonDeductible?: number;
      otherTransportFares?: number;
      otherUniform?: number;
      otherPrivateVehicle?: number;
    };
    deductions?: {
      preTaxFees?: number;
      preTaxWorkplaceGiving?: number;
      postTaxFees?: number;
      postTaxWorkplaceGiving?: number;
      childSupportGarnishees?: number;
      childSupportDeductions?: number;
    };
    fringeBenefits?: {
      fringeBenefitsTaxable?: number;
    };
  };
  template: {
    ordinaryTimeEarnings: {
      hours: number;
      amount: number;
    };
    earnings: Array<{
      type: string;
      exemptFromSuper: boolean;
      amount: number;
    }>;
    extraHours: Array<{
      option: string;
      name: string;
      hours: number;
      rate: number;
    }>;
    allowances: Array<{
      unit: number;
      rate: number;
      option: string;
      type: string;
    }>;
    deductions: Array<{
      option: string;
      type: string;
      rate: number;
    }>;
    childSupport: Array<{
      type: string;
      rate: number;
    }>;
    superannuation: {
      employerContribution: number;
      superSalarySacrifice: number;
      isPercentage: boolean;
    };
    fixedTaxAmount: {
      enabled: boolean;
      amount: number;
    };
  };
}

const tabLabels = [
  'Personal',
  'Bank and Super',
  'Employment',
  'Pay rates',
  'Tax',
  'Opening balances',
  'Template'
];

interface TabProps {
  onSave: () => void;
  setTabIndex: (index: number) => void;
}

const tabComponents: React.FC<TabProps>[] = [
  PersonalTab,
  BankAndSuperTab,
  EmploymentTab,
  PayRatesTab,
  TaxTab,
  OpeningBalancesTab,
  TemplateTab
];

interface EmployeeDetailsModalProps {
  open: boolean;
  onClose: () => void;
  screening?: Screening | null;
}

const EmployeeDetailsModal: React.FC<EmployeeDetailsModalProps> = ({
  open,
  onClose,
  screening
}) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [fullDetails, setFullDetails] = useState<EmployeeDetailsForm>(
    employeeDetailsDefaults as EmployeeDetailsForm
  );
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const dispatch = useAppDispatch();
  const isSmallScreen = window.innerWidth < 600;
  const isMediumScreen = window.innerWidth >= 600 && window.innerWidth < 900;

  const selectedProjectId = useAppSelector((state) => state.homeTilesServices.selectedProjectId);
  const selectedServiceTitle = useAppSelector(
    (state) => state.serviceSelected.selectedServiceTitle
  );
  const { success, message } = useAppSelector((state) => state.people);

  const methods = useForm<EmployeeDetailsForm>({
    resolver: yupResolver(employeeDetailsSchema) as any,
    mode: 'onChange',
    defaultValues: fullDetails
  });

  const getFontSize = (small: string, medium: string, large: string) => {
    if (isSmallScreen) return small;
    if (isMediumScreen) return medium;
    return large;
  };

  const fontSizes = {
    title: getFontSize('0.95rem', '1rem', '1.05rem'),
    employee: getFontSize('0.8rem', '0.85rem', '0.9rem'),
    tab: getFontSize('0.75rem', '0.8rem', '0.85rem'),
    close: getFontSize('1rem', '1.125rem', '1.25rem')
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const tabFieldNames = [
    // Personal
    [
      'personal.firstName',
      'personal.lastName',
      'personal.email',
      'personal.phone',
      'personal.gender',
      'personal.address1',
      'personal.address2',
      'personal.suburb',
      'personal.state',
      'personal.postcode'
    ] as const,
    // Bank and Super
    [
      'bankAndSuper.paymentMethod',
      'bankAndSuper.accountName',
      'bankAndSuper.bank',
      'bankAndSuper.bsb',
      'bankAndSuper.accountNumber',
      'bankAndSuper.superFundType',
      'bankAndSuper.superContributionRate',
      'bankAndSuper.superCompany',
      'bankAndSuper.smsfsName',
      'bankAndSuper.smsfsAbn',
      'bankAndSuper.electronicServiceAddress',
      'bankAndSuper.employerNumber',
      'bankAndSuper.smsfsBankAccountName',
      'bankAndSuper.smsfsBsb',
      'bankAndSuper.smsfsAccountNumber'
    ] as const,
    // Employment
    [
      'employment.jobTitle',
      'employment.startDate',
      'employment.employmentType',
      'employment.weeklyWorkHours',
      'employment.roles',
      'employment.payPeriod',
      'employment.leaveLoading'
    ] as const,
    // Pay rates
    [
      'payRates.basePayRate',
      'payRates.basePayRateType',
      'payRates.saturdayRate',
      'payRates.saturdayRateType',
      'payRates.sundayRate',
      'payRates.sundayRateType',
      'payRates.publicHolidayRate',
      'payRates.publicHolidayRateType',
      'payRates.additionalPublicHoliday',
      'payRates.additionalPublicHolidayRateType',
      'payRates.nightShiftRate',
      'payRates.nightShiftRateType',
      'payRates.nightShiftStartTime',
      'payRates.nightShiftEndTime',
      'payRates.holidays'
    ] as const,
    // Tax
    [
      'tax.dateOfBirth',
      'tax.statusOfResidency',
      'tax.incomeType',
      'tax.tfnExemption',
      'tax.taxFileNumber',
      'tax.claimTaxFreeThreshold',
      'tax.hasStudyAndTrainingLoan',
      'tax.isHorticulturistOrShearer',
      'tax.hasChildSupportObligations'
    ] as const,
    // Opening balances
    (
      [
        'asAtDate',
        'leaveBalances',
        'ordinaryEarnings',
        'paidLeave',
        'allowance',
        'deductions',
        'fringeBenefits'
      ] as const
    ).flatMap((key) =>
      Object.keys(employeeDetailsDefaults.openingBalances[key] || {}).map(
        (subKey) => `openingBalances.${key}.${subKey}`
      )
    ) as Array<`openingBalances.${
      | 'asAtDate'
      | 'leaveBalances'
      | 'ordinaryEarnings'
      | 'paidLeave'
      | 'allowance'
      | 'deductions'
      | 'fringeBenefits'}.${string}`>,
    // Template
    [
      'template.ordinaryTimeEarnings.hours',
      'template.ordinaryTimeEarnings.amount',
      'template.earnings[0].type',
      'template.earnings[0].exemptFromSuper',
      'template.earnings[0].amount',
      'template.extraHours[0].option',
      'template.extraHours[0].name',
      'template.extraHours[0].hours',
      'template.extraHours[0].rate',
      'template.allowances[0].unit',
      'template.allowances[0].rate',
      'template.allowances[0].option',
      'template.allowances[0].type',
      'template.deductions[0].option',
      'template.deductions[0].type',
      'template.deductions[0].rate',
      'template.childSupport[0].type',
      'template.childSupport[0].rate',
      'template.superannuation.employerContribution',
      'template.superannuation.superSalarySacrifice',
      'template.superannuation.isPercentage',
      'template.fixedTaxAmount.enabled',
      'template.fixedTaxAmount.amount'
    ] as const
  ];

  const handleSave = async () => {
    const fieldsToValidate = tabFieldNames[tabIndex];
    const isValid = await methods.trigger(fieldsToValidate as any);
    if (!isValid) {
      console.log('Validation errors:', methods.formState.errors);
      setSnackbar({
        open: true,
        message: 'Please correct the validation errors before saving.',
        severity: 'error'
      });
      return;
    }

    const data = methods.getValues();
    setFullDetails(data);

    console.log('Saving full payload:', data);

    const payload = {
      group_id: selectedProjectId || '',
      service_name_id: selectedServiceTitle || '',
      employeeId: screening?.employeeId || '0',
      email_key: screening?.email || '0',
      details: data
    };

    const result = await dispatch(saveEmployeeDetails(payload));

    if (saveEmployeeDetails.fulfilled.match(result) && selectedProjectId && selectedServiceTitle) {
      // Always refresh the screenings list
      dispatch(
        listScreenings({
          group_id: selectedProjectId,
          service_id: selectedServiceTitle,
          page_size: 10
        })
      );

      // Always refetch the employee data to get the latest saved version
      await fetchAndPatchData();

      dispatch(resetPeopleState());

      setSnackbar({
        open: true,
        message: 'Employee details saved successfully!',
        severity: 'success'
      });
    } else {
      console.error('Failed to save employee details:', result.payload);
      setSnackbar({
        open: true,
        message: 'Failed to save employee details. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const fetchAndPatchData = async () => {
    try {
      const action = await dispatch(
        fetchEmployeeScreening({
          group_id: selectedProjectId || '',
          service_id: selectedServiceTitle || 'People_Screening',
          email_key: screening!.email,
          id: screening!.employeeId,
          isNewRecord: screening!.isNewRecord || false
        })
      );
      if (fetchEmployeeScreening.fulfilled.match(action)) {
        const response = action.payload;
        if (response?.details) {
          console.log('Patching form with startDate:', response.details.employment?.startDate);
          methods.reset({
            personal: {
              firstName: response.details.personal?.firstName || '',
              lastName: response.details.personal?.lastName || '',
              email: response.details.personal?.email || '',
              phone: response.details.personal?.phone || '',
              gender: response.details.personal?.gender || '',
              address1: response.details.personal?.address1 || '',
              address2: response.details.personal?.address2 || '',
              suburb: response.details.personal?.suburb || '',
              state: response.details.personal?.state || '',
              postcode: response.details.personal?.postcode || ''
            },
            bankAndSuper: {
              paymentMethod: response.details.bankAndSuper?.paymentMethod || '',
              accountName: response.details.bankAndSuper?.accountName || '',
              bank: response.details.bankAndSuper?.bank || '',
              bsb: response.details.bankAndSuper?.bsb || '',
              accountNumber: response.details.bankAndSuper?.accountNumber || '',
              superFundType: response.details.bankAndSuper?.superFundType || '',
              superContributionRate: response.details.bankAndSuper?.superContributionRate || '',
              superCompany: response.details.bankAndSuper?.superCompany || '',
              smsfsName: response.details.bankAndSuper?.smsfsName || '',
              smsfsAbn: response.details.bankAndSuper?.smsfsAbn || '',
              electronicServiceAddress:
                response.details.bankAndSuper?.electronicServiceAddress || '',
              employerNumber: response.details.bankAndSuper?.employerNumber || '',
              smsfsBankAccountName: response.details.bankAndSuper?.smsfsBankAccountName || '',
              smsfsBsb: response.details.bankAndSuper?.smsfsBsb || '',
              smsfsAccountNumber: response.details.bankAndSuper?.smsfsAccountNumber || ''
            },
            employment: {
              jobTitle: response.details.employment?.jobTitle || '',
              startDate: response.details.employment?.startDate || '',
              employmentType: response.details.employment?.employmentType || '',
              weeklyWorkHours: response.details.employment?.weeklyWorkHours || '',
              roles: response.details.employment?.roles || '',
              payPeriod: response.details.employment?.payPeriod || '',
              leaveLoading: response.details.employment?.leaveLoading || ''
            },
            payRates: {
              basePayRate: response.details.payRates?.basePayRate || '',
              basePayRateType: response.details.payRates?.basePayRateType || 'hour',
              saturdayRate: response.details.payRates?.saturdayRate || '',
              saturdayRateType: response.details.payRates?.saturdayRateType || 'hour',
              sundayRate: response.details.payRates?.sundayRate || '',
              sundayRateType: response.details.payRates?.sundayRateType || 'hour',
              publicHolidayRate: response.details.payRates?.publicHolidayRate || '',
              publicHolidayRateType: response.details.payRates?.publicHolidayRateType || 'hour',
              additionalPublicHoliday: response.details.payRates?.additionalPublicHoliday || '',
              additionalPublicHolidayRateType:
                response.details.payRates?.additionalPublicHolidayRateType || 'hour',
              nightShiftRate: response.details.payRates?.nightShiftRate || '',
              nightShiftRateType: response.details.payRates?.nightShiftRateType || 'hour',
              nightShiftStartTime: response.details.payRates?.nightShiftStartTime || '',
              nightShiftEndTime: response.details.payRates?.nightShiftEndTime || '',
              holidays: response.details.payRates?.holidays || []
            },
            tax: {
              dateOfBirth: response.details.tax?.dateOfBirth || '',
              statusOfResidency: response.details.tax?.statusOfResidency || '',
              incomeType: response.details.tax?.incomeType || '',
              tfnExemption: response.details.tax?.tfnExemption || '',
              taxFileNumber: response.details.tax?.taxFileNumber || '',
              claimTaxFreeThreshold: response.details.tax?.claimTaxFreeThreshold || false,
              hasStudyAndTrainingLoan: response.details.tax?.hasStudyAndTrainingLoan || false,
              isHorticulturistOrShearer: response.details.tax?.isHorticulturistOrShearer || false,
              hasChildSupportObligations: response.details.tax?.hasChildSupportObligations || false
            },
            openingBalances: {
              asAtDate: response.details.openingBalances?.asAtDate || '',
              leaveBalances: {
                annualLeaveBalance:
                  response.details.openingBalances?.leaveBalances?.annualLeaveBalance || '',
                sickLeaveBalance:
                  response.details.openingBalances?.leaveBalances?.sickLeaveBalance || ''
              },
              ordinaryEarnings: {
                ordinaryTimeEarnings:
                  response.details.openingBalances?.ordinaryEarnings?.ordinaryTimeEarnings || '',
                superGuarantee:
                  response.details.openingBalances?.ordinaryEarnings?.superGuarantee || '',
                tax: response.details.openingBalances?.ordinaryEarnings?.tax || '',
                bonusesCommission:
                  response.details.openingBalances?.ordinaryEarnings?.bonusesCommission || '',
                directorsFee:
                  response.details.openingBalances?.ordinaryEarnings?.directorsFee || '',
                employerContributionsRESC:
                  response.details.openingBalances?.ordinaryEarnings?.employerContributionsRESC ||
                  '',
                superSalarySacrifice:
                  response.details.openingBalances?.ordinaryEarnings?.superSalarySacrifice || '',
                reimbursement:
                  response.details.openingBalances?.ordinaryEarnings?.reimbursement || '',
                overtime: response.details.openingBalances?.ordinaryEarnings?.overtime || ''
              },
              paidLeave: {
                annualLeave: response.details.openingBalances?.paidLeave?.annualLeave || '',
                sickLeave: response.details.openingBalances?.paidLeave?.sickLeave || '',
                longServiceLeave:
                  response.details.openingBalances?.paidLeave?.longServiceLeave || '',
                paidParentalLeave:
                  response.details.openingBalances?.paidLeave?.paidParentalLeave || '',
                ancillaryAndDefenceLeave:
                  response.details.openingBalances?.paidLeave?.ancillaryAndDefenceLeave || '',
                workersCompensation:
                  response.details.openingBalances?.paidLeave?.workersCompensation || ''
              },
              allowance: {
                awardTransportPayments:
                  response.details.openingBalances?.allowance?.awardTransportPayments || '',
                centsPerKM: response.details.openingBalances?.allowance?.centsPerKM || '',
                laundry: response.details.openingBalances?.allowance?.laundry || '',
                overtimeMeal: response.details.openingBalances?.allowance?.overtimeMeal || '',
                qualifications: response.details.openingBalances?.allowance?.qualifications || '',
                task: response.details.openingBalances?.allowance?.task || '',
                tool: response.details.openingBalances?.allowance?.tool || '',
                travel: response.details.openingBalances?.allowance?.travel || '',
                jobKeeperTopUp: response.details.openingBalances?.allowance?.jobKeeperTopUp || '',
                otherGeneral: response.details.openingBalances?.allowance?.otherGeneral || '',
                otherHomeOffice: response.details.openingBalances?.allowance?.otherHomeOffice || '',
                otherNonDeductible:
                  response.details.openingBalances?.allowance?.otherNonDeductible || '',
                otherTransportFares:
                  response.details.openingBalances?.allowance?.otherTransportFares || '',
                otherUniform: response.details.openingBalances?.allowance?.otherUniform || '',
                otherPrivateVehicle:
                  response.details.openingBalances?.allowance?.otherPrivateVehicle || ''
              },
              deductions: {
                preTaxFees: response.details.openingBalances?.deductions?.preTaxFees || '',
                preTaxWorkplaceGiving:
                  response.details.openingBalances?.deductions?.preTaxWorkplaceGiving || '',
                postTaxFees: response.details.openingBalances?.deductions?.postTaxFees || '',
                postTaxWorkplaceGiving:
                  response.details.openingBalances?.deductions?.postTaxWorkplaceGiving || '',
                childSupportGarnishees:
                  response.details.openingBalances?.deductions?.childSupportGarnishees || '',
                childSupportDeductions:
                  response.details.openingBalances?.deductions?.childSupportDeductions || ''
              },
              fringeBenefits: {
                fringeBenefitsTaxable:
                  response.details.openingBalances?.fringeBenefits?.fringeBenefitsTaxable || ''
              }
            },
            template: response.details.template || {}
          });
        } else {
          console.error('Failed to fetch employee screening:', action.payload);
        }
      }
    } catch (error) {
      console.error('Failed to fetch employee screening:', error);
    }
  };

  useEffect(() => {
    if (open && screening?.employeeId && screening?.email) {
      fetchAndPatchData();
    } else {
      methods.reset(employeeDetailsDefaults as EmployeeDetailsForm);
      setFullDetails(employeeDetailsDefaults as EmployeeDetailsForm);
    }
  }, [open, screening, selectedProjectId, selectedServiceTitle, dispatch, methods]);

  const TabComponent = tabComponents[tabIndex];

  return (
    <FormProvider {...methods}>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            borderRadius: isSmallScreen ? 0 : '8px',
            maxWidth: isSmallScreen ? '95%' : isMediumScreen ? 'sm' : 'md',
            margin: isSmallScreen ? 0 : 'auto',
            maxHeight: isSmallScreen ? '80%' : 'calc(100% - 64px)'
          }
        }}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 0 }}
        >
          <Box>
            <span style={{ fontWeight: 700, fontSize: fontSizes.title }}>Employment details</span>
            {screening?.employeeName && (
              <span
                style={{
                  marginLeft: 12,
                  fontWeight: 400,
                  fontSize: fontSizes.employee,
                  color: '#555'
                }}
              >
                {screening?.employeeName}
              </span>
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon sx={{ fontSize: fontSizes.close }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 0, px: isSmallScreen ? 1 : isMediumScreen ? 2 : 4 }}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2, borderBottom: '1px solid #eee', minHeight: isSmallScreen ? 28 : 32 }}
          >
            {tabLabels.map((label, idx) => (
              <Tab
                key={label}
                label={label}
                sx={{
                  minWidth: isSmallScreen ? 70 : 100,
                  fontWeight: tabIndex === idx ? 700 : 500,
                  fontSize: fontSizes.tab,
                  py: isSmallScreen ? 0.25 : 0.5
                }}
              />
            ))}
          </Tabs>
          <Box sx={{ mt: 1 }}>
            <TabComponent onSave={handleSave} setTabIndex={setTabIndex} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button type="button" variant="contained" size="small" onClick={handleSave}>
              Save
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      {createPortal(
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ zIndex: 9999 }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>,
        document.body
      )}
    </FormProvider>
  );
};

export default EmployeeDetailsModal;
